/**
 * AUT-02: Página de Selección de Tenant
 *
 * Se muestra cuando el usuario pertenece a múltiples tenants.
 * Permite cambiar de empresa también durante la sesión.
 */
import { useNavigate, Navigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, ChevronRight, LogOut, Shield } from 'lucide-react'
import { useAuthStore, type TenantInfo } from '@/store/authStore'
import { buildTenantSwitchUrl } from '@/lib/tenantNav'
import { descartarDestino, tomarDestino } from '@/lib/destinoTrasLogin'
import { ROLE_NAMES, type RoleType } from '@/types'

export function SelectTenantPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, tenants, setActiveTenant, logout } = useAuthStore()

  // Platform admin sin tenants → redirigir al panel de plataforma
  if (tenants.length === 0 && user?.is_platform_admin) {
    return <Navigate to="/platform" replace />
  }

  const handleSelectTenant = (tenant: TenantInfo) => {
    queryClient.clear()
    setActiveTenant(tenant.schema)

    // Salto cross-subdominio (preserva el puerto) o mismo host. El SSO se apoya
    // en las cookies compartidas (.clobi.io); ?t= activa el tenant al llegar.
    const url = buildTenantSwitchUrl(tenant, tenants)
    if (url) {
      // Otra empresa, otro subdominio: el enlace que se estaba abriendo era de
      // esta, así que allá no aplica (#1656).
      descartarDestino()
      window.location.href = url
      return
    }

    navigate(tomarDestino() ?? '/dashboard', { replace: true })
  }

  const handleLogout = () => {
    logout()
    navigate('/auth/login', { replace: true })
  }

  // Si no hay tenants (no debería pasar, pero como seguro)
  if (tenants.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-auth px-4">
        <div className="w-full max-w-md bg-surface rounded-2xl p-8 text-center shadow-2xl">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-content mb-2">Sin acceso</h2>
          <p className="text-content-muted text-sm mb-6">
            No tiene acceso asignado a ninguna empresa activa.
            Por favor, contacte al administrador de su empresa.
          </p>
          <button
            onClick={handleLogout}
            className="w-full py-2.5 bg-surface-muted hover:bg-surface-muted text-content rounded-lg text-sm font-medium transition-colors"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-auth px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/clobi-logo-white.svg" alt="Clobi" className="h-28 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-white">Selecciona empresa</h2>
          <p className="text-content-subtle text-sm mt-1">
            Hola, <span className="text-white font-medium">{user?.full_name}</span>
          </p>
        </div>

        {/* Lista de tenants */}
        {/*
          ⚠️ Superficie SÓLIDA, no vidrio. Esta pantalla no vive sobre el canvas
          del tema sino sobre `bg-auth`, el degradado oscuro de la marca, que es
          el mismo en claro y en oscuro. Un panel translúcido deja pasar ese
          degradado y le come el contraste a todo lo secundario —el nombre del
          rol, «Tienes acceso a N empresas», el pie de la vista consolidada—,
          que quedaban ilegibles. Es la regla del propio design system: lo que
          flota sobre un fondo que no controla va sólido.

          🟢 Las otras 8 pantallas de autenticación ya son sólidas: esta era la
          única que el barrido del vidrio alcanzó.
        */}
        <div className="bg-surface rounded-card shadow-2xl overflow-hidden border border-divider-strong">
          <div className="px-6 py-4 border-b border-divider-strong">
            <p className="text-sm text-content-muted">
              {tenants.length === 1
                ? 'Tienes acceso a la siguiente empresa:'
                : `Tienes acceso a ${tenants.length} empresas:`}
            </p>
          </div>

          <ul className="divide-y divide-divider-strong">
            {tenants.map((tenant) => (
              <TenantCard
                key={tenant.schema}
                tenant={tenant}
                onSelect={() => handleSelectTenant(tenant)}
              />
            ))}
          </ul>

          {/* Consolidated link — solo si 2+ tenants tienen permiso consolidation:view */}
          {(() => {
            const withConsolidation = tenants.filter(t => {
              const perms = t.permissions || {}
              return perms['consolidation']?.includes('view') ||
                     perms['reports']?.includes('view')  // fallback: si tiene reports en al menos 2
            })
            if (withConsolidation.length < 2) return null
            return (
              <div className="px-5 pt-3">
                <button
                  onClick={() => navigate('/consolidated')}
                  className="w-full flex items-center gap-3 px-5 py-3 rounded-xl border border-dashed border-divider-strong text-content-muted hover:text-primary hover:border-primary-200 hover:bg-primary-50/50 transition-colors mt-3"
                >
                  <Building2 size={18} />
                  <div className="text-left">
                    <p className="text-sm font-medium">Vista Consolidada</p>
                    <p className="text-xs text-content-subtle">Ver métricas de {withConsolidation.length} empresas</p>
                  </div>
                </button>
              </div>
            )
          })()}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-divider-strong">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-content-muted hover:text-danger-content-soft transition-colors rounded-lg hover:bg-danger-surface"
            >
              <LogOut size={14} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── TenantCard ───────────────────────────────────────────────────────────────
interface TenantCardProps {
  tenant: TenantInfo
  onSelect: () => void
}

function TenantCard({ tenant, onSelect }: TenantCardProps) {
  const isAdmin = tenant.roles.includes('admin')
  const primaryRole = tenant.roles[0] as RoleType

  return (
    <li>
      <button
        onClick={onSelect}
        className="w-full flex items-center gap-4 px-6 py-4 hover:bg-primary-50 transition-colors text-left group"
      >
        {/* Avatar de empresa */}
        {tenant.logo_url ? (
          <img src={tenant.logo_url} alt="" className="w-10 h-10 rounded-xl object-contain bg-surface-muted shrink-0 shadow-sm group-hover:shadow-md transition-shadow" />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
            <Building2 size={18} className="text-white" />
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-content text-sm truncate">{tenant.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {isAdmin && (
              <span className="inline-flex items-center gap-0.5 text-xs text-warning-content-soft bg-warning-surface px-1.5 py-0.5 rounded-full font-medium">
                <Shield size={10} />
                Admin
              </span>
            )}
            <span className="text-xs text-content-subtle">
              {tenant.roles
                .slice(0, 2)
                .map((r) => ROLE_NAMES[r as RoleType] || r)
                .join(', ')}
              {tenant.roles.length > 2 && ` +${tenant.roles.length - 2}`}
            </span>
          </div>
        </div>

        <ChevronRight size={16} className="text-content-subtle group-hover:text-primary transition-colors shrink-0" />
      </button>
    </li>
  )
}
